import { Breadcrumb, Button, Layout, Popover, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { administratorsListAction } from '~Administrators/actions';
import { administratorsActiveSelector, administratorsSelector } from '~Administrators/selectors';
import IAdministrator from '~Api/Administrator/IAdministrator';
import RoleEnum from '~Api/Administrator/RoleEnum';
import AccountTypeEnum from '~Api/Investor/AccountTypeEnum';
import IAccount from '~Api/Investor/IAccount';
import IInvestor from '~Api/Investor/IInvestor';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import {
    investorsListSettingsSetAction,
    investorsPaginatedListAction,
} from './actions';
import AccountTypeTag from './Investor/AccountTypeTag';
import ImSelector from './Investor/ImSelector';
import './investors.less';
import Search from './Search';
import {
    investorsListSettingsSelector,
    investorsPaginatedCountSelector,
    investorsPaginatedSelector,
} from './selectors';
import accountTypeLabels from '~Api/Investor/accountTypeLabels';
import ListSortFieldEnum from '~Api/Investor/ListSortFieldEnum';
import ListSortOrderEnum from '~Api/Investor/ListSortOrderEnum';
import { FilterValue, SortOrder, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';

export interface IInvestorsListSettings {
    accountTypes: AccountTypeEnum[];
    currentPage: number;
    investorManagerUuids: string[];
    pageSize: number;
    sortField: ListSortFieldEnum;
    sortOrder: SortOrder;
}

const tableSortFieldMappings: IDictionary<ListSortFieldEnum> = {
    createdTime: ListSortFieldEnum.CreatedTime,
    investorManager: ListSortFieldEnum.InvestorManager,
};

const tableSortOrderMappings: IDictionary<ListSortOrderEnum> = {
    ascend: ListSortOrderEnum.Ascending,
    descend: ListSortOrderEnum.Descending,
};

interface IPropsSelector {
    activeAdministrators: IAdministrator[];
    administrators: IDictionary<IAdministrator>;
    investors: IDictionary<IInvestor>;
    investorsListSettings: IInvestorsListSettings;
    investorsPaginatedCount: number;
}

interface IPropsDispatch {
    administratorsList: () => void;
    investorsListSettingsSet: (
        accountTypes: AccountTypeEnum[],
        currentPage: number,
        investorManagerUuids: string[],
        pageSize: number,
        sortField: ListSortFieldEnum,
        sortOrder: SortOrder,
    ) => void;
    investorsPaginatedList: (
        accountTypes: AccountTypeEnum[],
        currentPage: number,
        investorManagerUuids: string[],
        pageSize: number,
        sortField: ListSortFieldEnum,
        sortOrder: ListSortOrderEnum,
    ) => void;
}

type Props = IPropsSelector & IPropsDispatch;

class List extends React.Component<Props> {
    private refreshInterval: any;

    constructor(props: Props) {
        super(props);

        this.onChangeTable = this.onChangeTable.bind(this);
    }

    public componentDidMount(): void {
        const {
            administrators,
            investors,
            investorsListSettings,
        } = this.props;

        if (!investors) {
            this.props.investorsPaginatedList(
                investorsListSettings.accountTypes,
                investorsListSettings.currentPage,
                investorsListSettings.investorManagerUuids,
                investorsListSettings.pageSize,
                ListSortFieldEnum.CreatedTime,
                ListSortOrderEnum.Descending,
            );
        }

        if (!administrators) {
            this.props.administratorsList();
        }

    }

    public componentWillUnmount(): void {
        clearInterval(this.refreshInterval);
    }

    public render(): JSX.Element {
        const {
            activeAdministrators,
            administrators,
            investors,
            investorsListSettings,
            investorsPaginatedCount,
        } = this.props;

        if (!activeAdministrators || !administrators) {
            return (
                <Layout className='investors'>
                    <Breadcrumb className='breadcrumb'>
                        <Breadcrumb.Item>Home</Breadcrumb.Item>
                        <Breadcrumb.Item>Investors</Breadcrumb.Item>
                    </Breadcrumb>
                    <Layout className='content-wrapper'>
                        <Layout.Content className='content'>
                            <Typography.Title level={2}>Investors</Typography.Title>
                            <Spin />
                        </Layout.Content>
                    </Layout>
                </Layout>
            );
        }

        const currencyFormatter = new Intl.NumberFormat('en-AU', {
            currency: 'AUD',
            style: 'currency',
        });

        const columns: ColumnType<IInvestor>[] = [
            {
                key: 'name',
                render: (investor: IInvestor) => {
                    return (
                        <React.Fragment>
                            <Link to={`/investors/${investor.uuid}`}>{investor.name || '-'}</Link>
                        </React.Fragment>
                    );
                },
                title: 'Name',
            },
            {
                filteredValue: investorsListSettings.accountTypes,
                filters: [
                    {
                        text: 'Marketplace',
                        value: AccountTypeEnum.Marketplace,
                    },
                    {
                        text: 'Income Trust',
                        value: AccountTypeEnum.IncomeTrust,
                    },
                ],
                key: 'accountType',
                render: (investor: IInvestor) => {
                    return (
                        <React.Fragment>
                            {_.map(investor.accounts, (account: IAccount) => <AccountTypeTag key={account.uuid} account={account} />)}
                        </React.Fragment>
                    );
                },
                title: 'Account Type',
                width: '15%',
            },
            {
                key: 'balance',
                render: (investor: IInvestor) => {
                    const popoverContent: JSX.Element[] = _.map(investor.accounts, (account: IAccount) => <p>{accountTypeLabels[account.accountType]}: {currencyFormatter.format(account.balance)}</p>);

                    return investor.balanceTotal !== 0 ? <Space className='balance-total'>{currencyFormatter.format(investor.balanceTotal)} <Popover content={popoverContent}><Tag>∑</Tag></Popover></Space> : '';
                },
                title: 'Balance',
                width: '10%',
            },
            {
                key: 'invested',
                render: (investor: IInvestor) => {
                    const popoverContent: JSX.Element[] = _.map(investor.accounts, (account: IAccount) => <p>{accountTypeLabels[account.accountType]}: {currencyFormatter.format(account.investedAmountCurrent)}</p>);

                    return investor.investedAmountCurrentTotal !== 0 ? <Space>{currencyFormatter.format(investor.investedAmountCurrentTotal)} <Popover content={popoverContent}><Tag>∑</Tag></Popover></Space> : '';
                },
                title: 'Invested',
                width: '10%',
            },
            {
                filteredValue: investorsListSettings.investorManagerUuids,
                filters: _.map(_.filter(activeAdministrators, (administrator: IAdministrator) => [ RoleEnum.InvestorManager, RoleEnum.SeniorInvestorManager ].includes(administrator.role)), (administrator: IAdministrator) => ({
                    text: administrator.name,
                    value: administrator.uuid,
                })),
                key: 'investorManager',
                render: (investor: IInvestor) => <ImSelector investor={investor} />,
                sortOrder: investorsListSettings.sortField === ListSortFieldEnum.InvestorManager ? investorsListSettings.sortOrder : null,
                sorter: true,
                title: 'IM',
                width: '20%',
            },
            {
                dataIndex: 'createdTime',
                defaultSortOrder: 'descend',
                key: 'createdTime',
                render: (createdTime: string) => createdTime ? <Tooltip title={dayjs(createdTime).format('Do MMMM YYYY HH:mm:ss')}><span>{dayjs(createdTime).fromNow()}</span></Tooltip> : '-',
                sortOrder: investorsListSettings.sortField === ListSortFieldEnum.CreatedTime ? investorsListSettings.sortOrder : null,
                sorter: true,
                title: 'Created',
                width: '10%',
            },
        ];

        return (
            <Layout className='investors'>
                <Breadcrumb className='breadcrumb'>
                    <Breadcrumb.Item>Home</Breadcrumb.Item>
                    <Breadcrumb.Item>Investors</Breadcrumb.Item>
                </Breadcrumb>
                <Layout className='content-wrapper'>
                    <Layout.Content className='content'>
                        <Space className='actions'>
                            <Search />
                            <Link to='/investors'><Button>Board View</Button></Link>
                        </Space>
                        <Typography.Title level={2}>Investors</Typography.Title>
                        <Table
                            columns={columns}
                            dataSource={_.values(investors)}
                            loading={!investors}
                            onChange={this.onChangeTable}
                            pagination={{ current: investorsListSettings.currentPage, pageSize: investorsListSettings.pageSize, showSizeChanger: true, total: investorsPaginatedCount }}
                            rowKey='uuid'
                            size='middle'
                        />
                    </Layout.Content>
                </Layout>
            </Layout>
        );
    }

    private onChangeTable(
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<IInvestor> | SorterResult<IInvestor>[],
    ): void {
        const { current, pageSize } = pagination;
        const accountTypeFilter: AccountTypeEnum[] = (filters.accountType as AccountTypeEnum[]) || [];
        const actualSorter: SorterResult<IInvestor> = sorter as SorterResult<IInvestor>;
        const investorMangerUuidsFilter: string[] = (filters.investorManager as string[]) || [];
        const sortField: ListSortFieldEnum = tableSortFieldMappings[actualSorter.columnKey] || ListSortFieldEnum.CreatedTime;

        this.props.investorsPaginatedList(
            accountTypeFilter,
            current,
            investorMangerUuidsFilter,
            pageSize,
            sortField,
            tableSortOrderMappings[actualSorter.order] || ListSortOrderEnum.Descending,
        );

        this.props.investorsListSettingsSet(
            accountTypeFilter,
            current,
            investorMangerUuidsFilter,
            pageSize,
            sortField,
            actualSorter.order,
        );
    }
}

function mapStateToProps(state: IGlobalState): IPropsSelector {
    return {
        activeAdministrators: administratorsActiveSelector(state),
        administrators: administratorsSelector(state),
        investors: investorsPaginatedSelector(state),
        investorsListSettings: investorsListSettingsSelector(state),
        investorsPaginatedCount: investorsPaginatedCountSelector(state),
    };
}

function mapDispatchToProps(dispatch: Dispatch): IPropsDispatch {
    return {
        administratorsList: () => dispatch(administratorsListAction()),
        investorsListSettingsSet: (
            accountTypes: AccountTypeEnum[],
            currentPage: number,
            investorManagerUuids: string[],
            pageSize: number,
            sortField: ListSortFieldEnum,
            sortOrder: SortOrder,
        ) => dispatch(investorsListSettingsSetAction({
            accountTypes,
            currentPage,
            investorManagerUuids,
            pageSize,
            sortField,
            sortOrder,
        })),
        investorsPaginatedList: (
            accountTypes: AccountTypeEnum[],
            currentPage: number,
            investorManagerUuids: string[],
            pageSize: number,
            sortField: ListSortFieldEnum,
            sortOrder: ListSortOrderEnum,
        ) => dispatch(investorsPaginatedListAction(
            accountTypes,
            investorManagerUuids,
            sortOrder,
            sortField,
            currentPage,
            pageSize,
        )),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(List);
